
import React, { useState } from "react";
import { RecipeIdea } from "@/components/admin/tools/recipe-ideas/types";

interface RecipeDetailsProps {
  recipe: RecipeIdea;
  themeClasses: {
    buttonClass: string;
  };
  onBack: () => void;
}

const RecipeDetails: React.FC<RecipeDetailsProps> = ({ 
  recipe, 
  themeClasses,
  onBack
}) => {
  const [activeTab, setActiveTab] = useState<'ingredients' | 'instructions'>('ingredients');
  
  return (
    <div className="space-y-4">
      <button 
        onClick={onBack} 
        className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        <span className="ml-1">Back to recipes</span>
      </button>
      
      <div className="border-b pb-4">
        <h2 className="text-2xl font-bold">{recipe.title}</h2>
        <p className="text-gray-600 mt-2">{recipe.description}</p>
        
        <div className="flex flex-wrap gap-2 mt-3">
          <span className="px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700">
            {recipe.difficulty}
          </span>
          <span className="px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700">
            {recipe.category}
          </span>
          {recipe.kidFriendly && (
            <span className="px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-700">
              Kid Friendly
            </span>
          )}
        </div>
      </div>
      
      <div className="flex flex-wrap gap-4 justify-between">
        <div className="border px-4 py-2 rounded text-center min-w-24">
          <span className="text-sm text-gray-500">Prep Time</span>
          <p className="font-medium">{recipe.prepTime}</p>
        </div>
        <div className="border px-4 py-2 rounded text-center min-w-24">
          <span className="text-sm text-gray-500">Cook Time</span>
          <p className="font-medium">{recipe.cookTime}</p>
        </div>
        <div className="border px-4 py-2 rounded text-center min-w-24">
          <span className="text-sm text-gray-500">Total Time</span>
          <p className="font-medium">{recipe.totalTime}</p>
        </div>
        <div className="border px-4 py-2 rounded text-center min-w-24">
          <span className="text-sm text-gray-500">Servings</span>
          <p className="font-medium">{recipe.servings}</p>
        </div>
      </div>
      
      <div className="mt-6">
        <div className="flex border-b mb-4">
          <button
            className={`py-2 px-4 ${activeTab === 'ingredients' ? 'border-b-2 border-green-500 font-medium' : 'text-gray-500'}`}
            onClick={() => setActiveTab('ingredients')}
          >
            Ingredients
          </button>
          <button
            className={`py-2 px-4 ${activeTab === 'instructions' ? 'border-b-2 border-green-500 font-medium' : 'text-gray-500'}`}
            onClick={() => setActiveTab('instructions')}
          >
            Instructions
          </button>
        </div>
        
        {activeTab === 'ingredients' ? (
          <div>
            <ul className="list-disc ml-5 space-y-2">
              {recipe.ingredients.map((ingredient, index) => (
                <li key={index}>{ingredient}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="whitespace-pre-line">
            {recipe.instructions}
          </div>
        )}
      </div>
      
      {recipe.tags.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-medium mb-2">Tags:</h3>
          <div className="flex flex-wrap gap-2">
            {recipe.tags.map((tag, index) => (
              <span key={index} className="px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700">
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipeDetails;
